import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import { Eye, Save, RotateCcw, Send, Code } from 'lucide-react';
import './EmailTemplate.css';

const EmailTemplate = () => {
  const [template, setTemplate] = useState({
    headerTitle: '🎵 Your Free Sample of the Day!',
    headerSubtitle: 'Discover something amazing',
    downloadButtonText: '⬇️ Listen & Download',
    warningText: '⚠️ <strong>Important:</strong> This link will expire in 48 hours! Don\'t miss out on these exclusive samples.',
    footerText: 'Come back tomorrow for more samples! 🎁',
    primaryColor: '#667eea',
    secondaryColor: '#764ba2',
    customHtml: '',
    useCustomHtml: false
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [testEmail, setTestEmail] = useState('');
  const [sending, setSending] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);

  useEffect(() => {
    fetchTemplate();
  }, []);

  const fetchTemplate = async () => {
    try {
      const response = await api.get('/template');
      const fetchedTemplate = response.data.template;
      
      // Merge fetched template with defaults to ensure all fields have values
      setTemplate(prev => ({
        headerTitle: fetchedTemplate.headerTitle || prev.headerTitle,
        headerSubtitle: fetchedTemplate.headerSubtitle || prev.headerSubtitle,
        downloadButtonText: fetchedTemplate.downloadButtonText || prev.downloadButtonText,
        warningText: fetchedTemplate.warningText || prev.warningText,
        footerText: fetchedTemplate.footerText || prev.footerText,
        primaryColor: fetchedTemplate.primaryColor || prev.primaryColor,
        secondaryColor: fetchedTemplate.secondaryColor || prev.secondaryColor,
        customHtml: fetchedTemplate.customHtml || '',
        useCustomHtml: fetchedTemplate.useCustomHtml || false
      }));
    } catch (error) {
      console.error('Error fetching template:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage('');

    try {
      await api.put('/template', template);
      setMessage('Template saved successfully!');
      setTimeout(() => setMessage(''), 3000);
    } catch (error) {
      setMessage('Error saving template: ' + (error.response?.data?.message || error.message));
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!window.confirm('Reset to default template? This will discard all customizations.')) {
      return;
    }

    try {
      const response = await api.post('/template/reset');
      setTemplate(response.data.template);
      setMessage('Template reset to default');
      setTimeout(() => setMessage(''), 3000);
    } catch (error) {
      setMessage('Error resetting template: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleSendTest = async () => {
    if (!testEmail) {
      setMessage('Please enter an email address');
      return;
    }

    setSending(true);
    setMessage('');

    try {
      await api.post('/template/test', { email: testEmail });
      setMessage(`Test email sent to ${testEmail}!`);
      setTestEmail('');
      setTimeout(() => setMessage(''), 5000);
    } catch (error) {
      setMessage('Error sending test: ' + (error.response?.data?.message || error.message));
    } finally {
      setSending(false);
    }
  };

  const handleChange = (field, value) => {
    setTemplate(prev => ({
      ...prev,
      [field]: value
    }));
  };

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  return (
    <div className="template-page">
      <div className="page-header">
        <div>
          <h2>Email Template Editor</h2>
          <p>Customize your daily sample emails • Save changes before previewing</p>
        </div>
        <div className="header-actions">
          <button 
            onClick={() => {
              const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
              window.open(`${apiUrl}/template/preview`, '_blank');
            }} 
            className="preview-btn"
          >
            <Eye size={20} />
            Preview Email
          </button>
          <button onClick={handleReset} className="reset-btn">
            <RotateCcw size={20} />
            Reset
          </button>
          <button onClick={handleSave} className="save-btn" disabled={saving}>
            <Save size={20} />
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>

      {message && (
        <div className={`message ${message.includes('Error') ? 'error' : 'success'}`}>
          {message}
        </div>
      )}

      <div className="template-layout-single">
        {/* Editor Panel */}
        <div className="editor-panel-full">
          <div className="editor-card">
            <h3>📝 Content Settings</h3>
            
            <div className="form-group">
              <label>Header Title</label>
              <input
                type="text"
                value={template.headerTitle}
                onChange={(e) => handleChange('headerTitle', e.target.value)}
                placeholder="🎵 Your Free Sample of the Day!"
              />
            </div>

            <div className="form-group">
              <label>Header Subtitle</label>
              <input
                type="text"
                value={template.headerSubtitle}
                onChange={(e) => handleChange('headerSubtitle', e.target.value)}
                placeholder="Discover something amazing"
              />
            </div>

            <div className="form-group">
              <label>Download Button Text</label>
              <input
                type="text"
                value={template.downloadButtonText}
                onChange={(e) => handleChange('downloadButtonText', e.target.value)}
                placeholder="⬇️ Download Now"
              />
            </div>

            <div className="form-group">
              <label>Warning Text (HTML allowed)</label>
              <textarea
                value={template.warningText}
                onChange={(e) => handleChange('warningText', e.target.value)}
                placeholder="⚠️ Important: This link will expire in 4 hours!"
                rows={3}
              />
            </div>

            <div className="form-group">
              <label>Footer Text</label>
              <input
                type="text"
                value={template.footerText}
                onChange={(e) => handleChange('footerText', e.target.value)}
                placeholder="Come back tomorrow for another surprise sample! 🎁"
              />
            </div>
          </div>

          <div className="editor-card">
            <h3>🎨 Color Settings</h3>
            
            <div className="color-row">
              <div className="form-group">
                <label>Primary Color</label>
                <div className="color-input">
                  <input
                    type="color"
                    value={template.primaryColor}
                    onChange={(e) => handleChange('primaryColor', e.target.value)}
                  />
                  <input
                    type="text"
                    value={template.primaryColor}
                    onChange={(e) => handleChange('primaryColor', e.target.value)}
                    placeholder="#667eea"
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Secondary Color</label>
                <div className="color-input">
                  <input
                    type="color"
                    value={template.secondaryColor}
                    onChange={(e) => handleChange('secondaryColor', e.target.value)}
                  />
                  <input
                    type="text"
                    value={template.secondaryColor}
                    onChange={(e) => handleChange('secondaryColor', e.target.value)}
                    placeholder="#764ba2"
                  />
                </div>
              </div>
            </div>

            <div className="gradient-preview" style={{
              background: `linear-gradient(135deg, ${template.primaryColor} 0%, ${template.secondaryColor} 100%)`
            }}>
              Gradient Preview
            </div>
          </div>

          <div className="editor-card">
            <h3>📧 Send Test Email</h3>
            <p className="help-text">Send a test email to preview how it looks in your inbox</p>
            
            <div className="test-email-row">
              <input
                type="email"
                value={testEmail}
                onChange={(e) => setTestEmail(e.target.value)}
                placeholder="your-email@example.com"
                className="test-email-input"
              />
              <button onClick={handleSendTest} className="send-test-btn" disabled={sending}>
                <Send size={18} />
                {sending ? 'Sending...' : 'Send Test'}
              </button>
            </div>
          </div>

          {/* Advanced Section */}
          <div className="editor-card">
            <div className="advanced-header" onClick={() => setShowAdvanced(!showAdvanced)}>
              <h3><Code size={20} /> Advanced: Custom HTML</h3>
              <span>{showAdvanced ? '▼' : '▶'}</span>
            </div>
            
            {showAdvanced && (
              <>
                <p className="help-text">
                  Use your own HTML template. Available variables: {'{'}{'{'} subscriberName {'}'}{'}'},  {'{'}{'{'} sampleTitle {'}'}{'}'},  {'{'}{'{'} downloadUrl {'}'}{'}'},  {'{'}{'{'} expiresAt {'}'}{'}'},  {'{'}{'{'} expiryTime {'}'}{'}'} 
                </p>
                
                <div className="form-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={template.useCustomHtml}
                      onChange={(e) => handleChange('useCustomHtml', e.target.checked)}
                    />
                    Use Custom HTML (overrides all settings above)
                  </label>
                </div>

                {template.useCustomHtml && (
                  <div className="form-group">
                    <label>Custom HTML Template</label>
                    <textarea
                      value={template.customHtml}
                      onChange={(e) => handleChange('customHtml', e.target.value)}
                      placeholder="Paste your custom HTML here..."
                      rows={15}
                      className="code-editor"
                    />
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmailTemplate;

